import { useState, useRef, useEffect } from 'react';

import Image from 'next/image'
import { useRouter } from 'next/router'

import styles from '../../styles/Register.module.scss'
import { motion } from 'framer-motion'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye, faEyeSlash, faHome, faXmarkCircle } from '@fortawesome/free-solid-svg-icons'

import Resizer from 'react-image-file-resizer'

import LoadingSpinner from '../../components/Account/LoadingSpinner/LoadingSpinner'

import axios from 'axios';

import { signIn, useSession } from 'next-auth/react';

import errorMsg from '../../components/Functions/errorMsg';

import Policies from '../../components/Home/Policies/Policies'
import Head from 'next/head';

import clarifaiApp from '../../mongodb/clarifaiClient'

export default function Register() {
  const router = useRouter();
  const { data: session, status } = useSession();

  useEffect(() => {
    if(status === 'authenticated') {
      router.push('/');
    }
  }, [status])

  const [pendingRequest, setPendingRequest] = useState(false)

  const submitButton = useRef();

  useEffect(() => {
    if(submitButton !== null && submitButton !== undefined) {
      if(submitButton.current !== null && submitButton.current !== undefined) {
        submitButton.current.disabled = true;
      }
    }
  }, [])

  const checkRef = useRef(null);

  const [registerUsername, setRegisterUsername] = useState('')
  const [registerEmail, setRegisterEmail] = useState('')
  const [registerPassword, setRegisterPassword] = useState('')
  const [registerPasswordConfirm, setRegisterPasswordConfirm] = useState('')
  
  const [termsAgree, setTermsAgreed] = useState(false);

  const [file, setFile] = useState(null)
  const [resizedData, setResizedData] = useState('');

  async function fileHandler(e) {
    if(typeof window === 'undefined') return;
    const heic2any = require('heic2any');

    const getFile = e.target.files[0];
    var fileSize = (getFile.size / (1024*1024)).toFixed(2);
    if(fileSize > 4) {
      errorMsg('Images must be no larger than 4mb');
      return;
    }

    let getRevisedFile

    if(getFile.name.includes('.HEIC')) {
      getRevisedFile = await heic2any({ blob: getFile, toType: 'image/jpeg' });
    } else {
      getRevisedFile = getFile;
    }

    const getBackendBase64Data = await resizeFile(getRevisedFile, 'base64');
    setResizedData(getBackendBase64Data);

    const getDisplayImg = await resizeFile(getRevisedFile, 'blob');

    const objectUrl = URL.createObjectURL(getDisplayImg);

    // Create a FileReader instance
    const reader = new FileReader();

    // Read the Blob as a data URL
    reader.readAsDataURL(getFile);

    // Define a callback function for when the reading is complete
    reader.onloadend = async () => {
      // Ensure the result is a string
      if (typeof reader.result !== 'string') {
        console.error('Invalid data URL:', reader.result);
        // Handle the error appropriately, e.g., return or throw an error
        return;
      }

      // Extract the base64 data from the data URL
      const base64Data = reader.result.split(',')[1];

      // Analyze image with Clarifai
      const clarifaiResponse = await clarifaiApp.models.predict(
        Clarifai.GENERAL_MODEL,
        { base64: base64Data }
      );

      const getConcepts = clarifaiResponse?.outputs[0]?.data?.concepts || [];

      const thresholds = {
        nude: 0.9,
        gun: 0.9,
        bomb: 0.9,
        gore: 0.9,
        danger: 0.9,
      };

      const filteredConcepts = getConcepts.filter(concept => {
        return (
          (concept.name === 'nude' && concept.value >= thresholds.nude) ||
          (concept.name === 'gun' && concept.value >= thresholds.gun) ||
          (concept.name === 'bomb' && concept.value >= thresholds.bomb) ||
          (concept.name === 'gore' && concept.value >= thresholds.gore) ||
          (concept.name === 'danger' && concept.value >= thresholds.danger)
        );
      });

      if (filteredConcepts.length > 0) {
        errorMsg("The images you're attempting to upload are against our policies.");
      } else {
        setFile(objectUrl);
      }

    };

    return () => URL.revokeObjectURL(objectUrl);
  }

  const resizeFile = async (file, type) =>
    new Promise((resolve, reject) => {
      Resizer.imageFileResizer(
        file,
        750,
        750,
        "PNG",
        100,
        0,
        (uri) => {
          resolve(uri);
        },
        `${type}`,
        300,
        0
      );
    });

  const submitRegister = async (e) => {
    e.preventDefault();
    if(pendingRequest === true) return;
    setPendingRequest(true)

    const userRegex = /^[A-Za-z0-9]*$/;

    if(!userRegex.test(registerUsername)) {
      errorMsg("Usernames must contain only letters and numbers.");
      setPendingRequest(false);
      checkRef.current.checked = false;
      setTermsAgreed(false)
      submitButton.current.disabled = true;
      return;
    }

    if(registerUsername.length <= 5 || registerUsername.length > 20) {
      errorMsg("Usernames are only allowed to be 6 to 20 characters.");
      setPendingRequest(false);
      checkRef.current.checked = false;
      setTermsAgreed(false)
      submitButton.current.disabled = true;
      return;
    }

    const regex = /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i;

    if(!registerEmail || !regex.test(registerEmail)){
      errorMsg("Please enter a structured email address: xxxx@xxx.xxx");
      setPendingRequest(false);
      checkRef.current.checked = false;
      setTermsAgreed(false)
      submitButton.current.disabled = true;
      return;
    }

    const passFormat = /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[#?!@$%^&*-]).{8,}$/;

    if(!passFormat.test(registerPassword)) {
      errorMsg("Your password does not meet specifications. Please include one uppercase, lowercase, number and special character (#?!@$%^&*-).");
      setPendingRequest(false);
      checkRef.current.checked = false;
      setTermsAgreed(false)
      submitButton.current.disabled = true;
      return;
    }

    if(registerPassword.length <= 6 || registerPassword.length > 128) {
      errorMsg("Your password must be between 7 characters and no more than 128 characters.");
      setPendingRequest(false);
      checkRef.current.checked = false;
      setTermsAgreed(false)
      submitButton.current.disabled = true;
      return;
    }

    if(registerPasswordConfirm.length <= 6 || registerPasswordConfirm.length > 128) {
      errorMsg("When confirming your password, it's less than 7 characters or greater than 128 characters.");
      setPendingRequest(false);
      checkRef.current.checked = false;
      setTermsAgreed(false)
      submitButton.current.disabled = true;
      return;
    }
    
    if(registerPassword !== registerPasswordConfirm) {
      errorMsg("The passwords you enter do not match, please double check what password you've entered and confirm it.");
      setPendingRequest(false);
      checkRef.current.checked = false;
      setTermsAgreed(false)
      submitButton.current.disabled = true;
      return;
    }

    if(termsAgree === false) {
      errorMsg("Please read and accept our Policies Page.")
      setPendingRequest(false);
      checkRef.current.checked = false;
      setTermsAgreed(false)
      submitButton.current.disabled = true;
      return;
    }

    setShowPassword(false);
    setShowConfirmPassword(false);

    const result = await axios.post(`${window.location.origin}/api/account/register`, {
      username: registerUsername,
      email: registerEmail,
      password: registerPassword,
      profile: resizedData,
      creationDate: new Date(),
    }).catch((error) => {
      if(error.response.status === 413) {
        errorMsg("ERROR: The image you uploaded is too large, please try a different image.");
        setPendingRequest(false);
        checkRef.current.checked = false;
        setTermsAgreed(false)
        submitButton.current.disabled = true;
      } else 
      if(error?.response?.data?.status !== undefined) {
        errorMsg(error?.response?.data?.status.toString());
        setPendingRequest(false);
        checkRef.current.checked = false;
        setTermsAgreed(false)
        submitButton.current.disabled = true;
      } else {
        errorMsg(error.toString());
        setPendingRequest(false);
        checkRef.current.checked = false;
        setTermsAgreed(false)
        submitButton.current.disabled = true;
      }
    })
    if(result !== undefined) {
      if(result.status === 200) {
        // Relogin and push to home.
        const res = await signIn("credentials", {
          redirect: false,
          username: registerUsername,
          password: registerPassword,
        })

        if(res === undefined) {
          errorMsg("There was an internal error when logging in, please try again later.");
          setPendingRequest(false);
        }
        
        if(res.error === null) {
          router.push('/')
        } else {
          setPendingRequest(false);
          if(res.error === 'ERROR_LOGIN_NO-USER') {
            errorMsg("There is no such user that exists in our database.")
          } else if (res.error === 'ERROR_LOGIN_CREDENTIALS') {
            errorMsg("Invalid username and password combination.");
          } else if (res.error === 'ERROR_LOGIN_BANNED') {
            errorMsg("You have been banned from using Waco Exchange. If you believe this is a mistake, please contact us.");
          } else if (res.error.includes('NOT_VERIFIED_')) {
            router.push('/account/verify/' + res.error.replace('NOT_VERIFIED_', ''))
          } else {
            errorMsg(res.error.toString());
          }
        }
        
      } else {
        errorMsg("There was an internal error when creating your account, please try again later.");
        setPendingRequest(false);
        checkRef.current.checked = false;
        setTermsAgreed(false)
        submitButton.current.disabled = true;
      }
    }

  }

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [showPolicies, setShowPolicies] = useState(false);

  return (
    <section style={{width: '100%', height: '100%'}}>

      <Head>
        <title>Waco Exchange - Register Today</title>
        <meta name="description" content="Create your Waco Exchange account today! Start browsing and posting in our expanding list of marketplaces like buying/selling items, real-estate, events and more." key="desc" />

        <meta property="og:title" content="Waco Exchange - Register Today" />
        <meta property="og:description" content="Create your Waco Exchange account today! Start browsing and posting in our expanding list of marketplaces like buying/selling items, real-estate, events and more." />
      </Head>

      { showPolicies === true && <section id={styles.policyWrapper}> <FontAwesomeIcon onClick={() => setShowPolicies(false)} icon={faXmarkCircle} id={styles.close} /> <Policies /></section> }

      <FontAwesomeIcon onClick={() => router.push('/')} icon={faHome} id={styles.goHome} />
      <div className={styles.registerWrapper}>
        <div>
          <span>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 1024" preserveAspectRatio="meet"><polygon points="163 0 0 0 0 88.61 34 121 163 0"/><polygon points="154 116 163 0 34 121 154 116"/><polygon points="413.05 0 163 0 348 67 413.05 0"/><polygon points="348 67 448 102 448 0 413.05 0 348 67"/><polygon points="312 231 448 210 448 102 348 67 312 231"/><polygon points="348 67 163 0 154 116 348 67"/><polygon points="312 231 348 67 154 116 312 231"/><polygon points="154 116 34 121 45.77 233.64 154 116"/><polygon points="48 231 312 231 154 116 48 231"/><polygon points="0 88.61 0 160 34 121 0 88.61"/><polygon points="34 121 0 157.86 0 330.37 62 389 34 121"/><polygon points="62 389 312 231 45.49 231 62 389"/><polygon points="201.18 411.36 312 231 209.23 295.95 201.18 411.36"/><polygon points="201.67 407.94 448 412 312 231 201.67 407.94"/><polygon points="448 412 448 210 312 231 448 412"/><polygon points="197 525 448 412 201.67 407.94 197 525"/><polygon points="62 389 197 525 209.23 295.95 62 389"/><polygon points="0 330.37 0 411.36 62 389 0 330.37"/><polygon points="27 605 197 525 62 389 27 605"/><polygon points="62 389 0 410.48 0 570.13 27 605 62 389"/><polygon points="122 786 197 525 27 605 122 786"/><polygon points="306.26 696.61 391 655.5 197 525 122 786 306.26 696.61"/><polygon points="391 655.5 448 412 197 525 391 655.5"/><polygon points="448 655.5 448 412 391 655.5 448 655.5"/><polygon points="448 837 448 655.5 391 655.5 448 837"/><polygon points="306.26 696.61 279 938 448 837 306.26 696.61"/><polygon points="306.26 696.61 448 837 391 655.5 306.26 696.61"/><polygon points="279 938 306.26 696.61 122 786 279 938"/><polygon points="122 786 0 740.91 0 861.44 16.25 913 122 786"/><polygon points="27 605 0 624.2 0 740.91 122 786 27 605"/><polygon points="0 570.13 0 624.2 27 605 0 570.13"/><polygon points="16.25 913 279 938 122 786 16.25 913"/><polygon points="0 952.43 0 1024 166 1024 16.25 913 0 952.43"/><polygon points="0 861.44 0 952.43 16.25 913 0 861.44"/><polygon points="16.25 913 162.7 1024 214.13 1024 279 938 16.25 913"/><polygon points="214.13 1024 448 1024 279 938 214.13 1024"/><polygon points="448 1024 448 837 279 938 448 1024"/></svg>
          </span>
          <section><h1>Create An Account</h1></section>
          <motion.section whileHover={{backgroundColor: '#154734', color: '#FFFFFF'}}>
            {
              file === null ? (<span><b>Profile Picture</b>(optional)<input disabled={pendingRequest === true ? true : false} type="file" onChange={e => fileHandler(e)} /></span>)
              :
              <span><FontAwesomeIcon onClick={() => { if(pendingRequest === false) setFile(null)}} icon={faXmarkCircle} id={styles.removeImg} /><Image fill style={{objectFit: 'cover'}} src={file} alt={file} /></span>
            }
          </motion.section>
          <section><input disabled={pendingRequest === true ? true : false} maxLength={14} onChange={e => setRegisterUsername(e.target.value)} type="text" placeholder="Create A Username" /></section>
          <section><input disabled={pendingRequest === true ? true : false} maxLength={50} onChange={e => setRegisterEmail(e.target.value)} pattern="" type="email" placeholder="Email Address" /></section>
          <section>
            <input disabled={pendingRequest === true ? true : false} onChange={e => setRegisterPassword(e.target.value)} type={showPassword === false ? 'password' : 'text'} title="Password must be minimum 7 characters. Must include at least, 1 Uppercase letter, 1 Lowercase letter, 1 Number, and 1 Symbol (#?!@$%^&*-)" pattern="^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[#?!@$%^&*-]).{8,}$" placeholder="Password" />
            <button disabled={pendingRequest === true ? true : false} onClick={(e) => { setShowPassword(state => !state); e.preventDefault() }}>
              {
                showPassword === true ? <FontAwesomeIcon icon={faEyeSlash} id={styles.icon} /> : <FontAwesomeIcon icon={faEye} id={styles.icon} />
              }
            </button>
          </section>
          <section>
            <input disabled={pendingRequest === true ? true : false} onChange={e => setRegisterPasswordConfirm(e.target.value)} type={showConfirmPassword === false ? 'password' : 'text'} placeholder="Confirm Password"/>
            <button disabled={pendingRequest === true ? true : false} onClick={(e) => { setShowConfirmPassword(state => !state); e.preventDefault()} }>
              {
                showConfirmPassword === true ? <FontAwesomeIcon icon={faEyeSlash} id={styles.icon} /> : <FontAwesomeIcon icon={faEye} id={styles.icon} />
              }
            </button>
          </section>
          <section><input ref={checkRef} disabled={pendingRequest === true ? true : false} onChange={(e) => {
            if(e.target.checked === true) {
              setTermsAgreed(true)
              submitButton.current.disabled = false;
            } else {
              setTermsAgreed(false)
              submitButton.current.disabled = true;
            }
            }} type='checkbox' />
          <p>By checking this box, I accept Waco Exchange's <button onClick={() => setShowPolicies(true)}>Policy & Conditions</button></p>
          </section>
          <section>
            <motion.button ref={submitButton} onClick={e => submitRegister(e)} whileHover={submitButton?.current?.disabled === false ? { border: '1px solid #338d6a', backgroundColor: '#FFFFFF', color: '#338d6a' } : {} } >
              {
                pendingRequest === true ?
                <LoadingSpinner type="ring" color="#FFFFFF" />
                :
                'Create Account'
              }
            </motion.button>
          </section>
        </div>
      </div>
      
    </section>
  )
}

function formatPhoneNumber(value) {
  if (!value) return value;

  const phoneNumber = value.replace(/[^\d]/g, '');
  const phoneNumberLength = phoneNumber.length;

  if (phoneNumberLength < 4) {
    return phoneNumber;
  }

  if (phoneNumberLength < 7) {
    return `(${phoneNumber.slice(0, 3)})-${phoneNumber.slice(3)}`;
  }

  return `(${phoneNumber.slice(0, 3)})-${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
}